import { AdminShell } from "@project/admin/components/admin-shell";
import { ApiResourceTable } from "@project/admin/components/api-resource-table";
import { CampaignFormFields } from "@project/admin/components/campaign-form-fields";
import { campaignFormToRecord } from "@project/admin/components/campaign-form-utils";
import type { Field } from "@project/admin/components/resource-table";
import { donationSchemeService } from "@project/services/admin/donation-scheme.service";
import {
  validateDonationSchemeAmount,
  type DonationSchemeAmountType,
  type DonationSchemePayload,
  type DonationSchemeRecord,
} from "@project/types/donation-scheme";

const tableFields: Field[] = [
  {
    name: "title",
    label: "Campaign Title",
    showInTable: true,
    hidden: true,
  },
  {
    name: "type_of_donation_name",
    label: "Donation Type",
    showInTable: true,
    hidden: true,
  },
  {
    name: "category_name",
    label: "Category",
    showInTable: true,
    hidden: true,
  },
  {
    name: "sub_category_title",
    label: "Sub Category",
    showInTable: true,
    hidden: true,
  },
  {
    name: "amount_needed",
    label: "Target (₹)",
    type: "number",
    showInTable: true,
    currency: true,
    hidden: true,
  },
  {
    name: "amount_collected",
    label: "Collected (₹)",
    type: "number",
    showInTable: true,
    currency: true,
    hidden: true,
  },
  {
    name: "date_status",
    label: "Status",
    showInTable: true,
    hidden: true,
  },
];

function buildCampaignPayload(
  payload: Record<string, unknown>
): DonationSchemePayload {
  const title = String(payload.title ?? "").trim();
  if (!title) {
    throw new Error("Title is required.");
  }

  if (!payload.donation_type) {
    throw new Error("Donation Type is required.");
  }

  if (!payload.main_category) {
    throw new Error("Main Category is required.");
  }

  if (!payload.sub_category) {
    throw new Error("Sub Category is required.");
  }

  if (!payload.start_date) {
    throw new Error("Start Date is required.");
  }

  if (!payload.end_date) {
    throw new Error("End Date is required.");
  }

  const amountNeeded = validateDonationSchemeAmount(
    payload.amount_needed,
    "Amount needed"
  );
  if (!amountNeeded) {
    throw new Error("Amount Needed is required.");
  }

  const amountType = String(
    payload.amount_type ?? "variable"
  ) as DonationSchemeAmountType;

  const base = {
    title,
    sub_title: payload.sub_title ? String(payload.sub_title).trim() : undefined,
    type_of_donation: Number(payload.donation_type),
    category: Number(payload.main_category),
    sub_category: Number(payload.sub_category),
    description: payload.description
      ? String(payload.description).trim()
      : undefined,
    amount_needed: amountNeeded,
    is_active: payload.is_active === true || payload.is_active === "true",
    start_date: String(payload.start_date).slice(0, 10),
    end_date: String(payload.end_date).slice(0, 10),
    banner_image:
      payload.banner_image instanceof File
        ? payload.banner_image
        : typeof payload.banner_image === "string" && payload.banner_image.trim()
          ? payload.banner_image.trim()
          : undefined,
    allow_donation_after_target:
      payload.allow_donation_after_target === true ||
      payload.allow_donation_after_target === "true",
    amount_type: amountType,
  };

  if (amountType === "fixed") {
    const fixedAmount = validateDonationSchemeAmount(
      payload.fixed_amount,
      "Fixed amount"
    );
    if (!fixedAmount) {
      throw new Error("Fixed Amount is required.");
    }
    return { ...base, fixed_amount: fixedAmount };
  }

  const minContribution = validateDonationSchemeAmount(
    payload.min_contribution,
    "Minimum contribution"
  );
  const maxContribution = validateDonationSchemeAmount(
    payload.max_contribution,
    "Maximum contribution"
  );
  if (
    minContribution &&
    maxContribution &&
    Number(minContribution) > Number(maxContribution)
  ) {
    throw new Error("Minimum contribution cannot exceed maximum contribution.");
  }

  return {
    ...base,
    min_contribution: minContribution,
    max_contribution: maxContribution,
  };
}

export default function Page() {
  return (
    <AdminShell
      title="Causes & Campaigns"
      subtitle="Time-bound fundraising drives with target & progress tracking"
    >
      <ApiResourceTable<DonationSchemeRecord>
        title="Campaign"
        labelField="title"
        fields={tableFields}
        searchFields={["title"]}
        defaultValues={{
          amount_type: "variable",
          is_active: true,
          allow_donation_after_target: false,
          min_contribution: "100",
          max_contribution: "100000",
        }}
        enableCsvExport
        csvFileName="donation-schemes"
        statusFilterOptions={[
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ]}
        totalField="amount_collected"
        addButtonLabel="Add Campaign"
        dialogClassName="max-w-3xl"
        getDialogTitle={editing =>
          (editing as Record<string, unknown>)?.id
            ? "Edit Donation Campaign"
            : "Create Donation Campaign"
        }
        mapRecordToForm={record =>
          campaignFormToRecord(record as Record<string, unknown>)
        }
        buildPayload={editing =>
          buildCampaignPayload(editing as Record<string, unknown>)
        }
        renderForm={({ editing, setEditing }) => (
          <CampaignFormFields
            editing={editing as Record<string, unknown> | null}
            setEditing={value =>
              setEditing(value as Partial<DonationSchemeRecord> | null)
            }
          />
        )}
        list={query => donationSchemeService.listDonationSchemesPage(query)}
        loadRecordForEdit={id => donationSchemeService.getDonationScheme(id)}
        create={payload =>
          donationSchemeService.create(payload as DonationSchemePayload)
        }
        update={(id, payload) =>
          donationSchemeService.update(id, payload as DonationSchemePayload)
        }
        remove={id => donationSchemeService.delete(id)}
      />
    </AdminShell>
  );
}
